import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  GraduationCap,
  Globe2,
  School,
  BookOpen,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Rocket,
  Layers,
  Trophy,
} from "lucide-react";

const STAGE_ICONS = {
  blank: Rocket,
  marco_teorico: Layers,
  review: Trophy,
};

const STAGE_DESCRIPTIONS = {
  blank: "Aún no escribes ni un capítulo. Empezamos con la estructura base.",
  marco_teorico: "Tienes algunos capítulos listos. Te ayudo a darles forma.",
  review: "El borrador está completo. Falta pulir, citar y validar.",
};

export default function OnboardingWizard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState({ countries: [], progress_stages: [] });

  const [form, setForm] = useState({
    country: "",
    university: "",
    catalog_id: null,
    career: "",
    thesis_title: "",
    progress_stage: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding/options");
        setOptions(data);
      } catch {
        toast.error("No se pudieron cargar las opciones");
      } finally {
        setLoadingOptions(false);
      }
    })();
  }, []);

  const universitiesForCountry = useMemo(() => {
    const c = options.countries.find((x) => x.country === form.country);
    return c?.universities || [];
  }, [options, form.country]);

  const canNext1 = form.country && form.university;
  const canNext2 = form.career.trim().length >= 2 && form.thesis_title.trim().length >= 3;
  const canSubmit = form.progress_stage;

  const next = () => setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/onboarding/complete", {
        country: form.country,
        university: form.university,
        catalog_id: form.catalog_id,
        career: form.career.trim(),
        thesis_title: form.thesis_title.trim(),
        progress_stage: form.progress_stage,
      });
      await refresh();
      toast.success("¡Tu espacio de tesis está listo!");
      navigate(`/dashboard/projects/${data.project_id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar tu información");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="onboarding-wizard"
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/45 backdrop-blur-md" />

      {/* Modal */}
      <div className="relative w-full max-w-2xl rounded-3xl bg-paper border border-rule shadow-[0_30px_80px_-20px_rgba(39,59,44,0.45)] overflow-hidden animate-fade-up">
        {/* Header */}
        <div className="px-7 sm:px-10 pt-8 pb-5 border-b border-rule bg-[radial-gradient(ellipse_at_top,rgba(193,148,86,0.10),transparent_70%)]">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-forest text-paper">
              <GraduationCap size={16} />
            </span>
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
                Configura tu espacio
              </p>
              <h2 className="font-serif text-xl sm:text-2xl text-ink leading-tight">
                Bienvenido a unaTESIS, {user?.name?.split(" ")[0] || "estudiante"}
              </h2>
            </div>
          </div>

          {/* Progress dots */}
          <div className="mt-5 flex items-center gap-2" data-testid="onboarding-progress">
            {[1, 2, 3].map((i) => {
              const done = i < step;
              const active = i === step;
              return (
                <span
                  key={i}
                  data-step={i}
                  data-active={active ? "true" : "false"}
                  data-done={done ? "true" : "false"}
                  className={`h-1.5 rounded-full transition-all ${
                    done
                      ? "bg-forest w-10"
                      : active
                      ? "bg-copper w-16"
                      : "bg-rule w-10"
                  }`}
                />
              );
            })}
            <span className="ml-auto text-[11px] text-ink-soft">{step}/3</span>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 sm:px-10 py-7 min-h-[300px]">
          {step === 1 && (
            <Step1
              loading={loadingOptions}
              countries={options.countries}
              universities={universitiesForCountry}
              country={form.country}
              setCountry={(c) =>
                setForm({ ...form, country: c, university: "", catalog_id: null })
              }
              university={form.university}
              setUniversity={(name, id) =>
                setForm({ ...form, university: name, catalog_id: id })
              }
            />
          )}

          {step === 2 && (
            <Step2
              career={form.career}
              setCareer={(v) => setForm({ ...form, career: v })}
              title={form.thesis_title}
              setTitle={(v) => setForm({ ...form, thesis_title: v })}
            />
          )}

          {step === 3 && (
            <Step3
              stages={options.progress_stages}
              value={form.progress_stage}
              setValue={(v) => setForm({ ...form, progress_stage: v })}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-7 sm:px-10 py-5 border-t border-rule flex items-center justify-between gap-3 bg-paper-2/40">
          {step > 1 ? (
            <button
              onClick={prev}
              disabled={submitting}
              data-testid="onboarding-back"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] text-ink-soft hover:bg-white"
            >
              <ArrowLeft size={13} /> Atrás
            </button>
          ) : (
            <span />
          )}

          {step < 3 ? (
            <button
              onClick={next}
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              data-testid="onboarding-next"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-50 shadow-[0_10px_30px_-12px_rgba(39,59,44,0.55)]"
            >
              Continuar
              <ArrowRight size={13} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!canSubmit || submitting}
              data-testid="onboarding-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(39,59,44,0.55)]"
            >
              {submitting ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Creando tu proyecto…
                </>
              ) : (
                <>
                  <Sparkles size={13} /> Crear mi espacio
                  <ArrowRight size={13} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 1 ---------- */
function Step1({ loading, countries, universities, country, setCountry, university, setUniversity }) {
  return (
    <div data-testid="onboarding-step-1">
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Paso 1 — Tu institución
      </p>
      <h3 className="mt-2 font-serif text-2xl text-ink leading-tight">
        ¿Dónde estudias?
      </h3>
      <p className="mt-2 text-[13px] text-ink-soft">
        Cargamos las plantillas oficiales y los formatos de citación que pide tu universidad.
      </p>

      {loading ? (
        <div className="mt-6 inline-flex items-center gap-2 text-[13px] text-ink-soft">
          <Loader2 size={14} className="animate-spin" /> Cargando opciones…
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          <Field icon={Globe2} label="País">
            <select
              data-testid="onboarding-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
            >
              <option value="">Selecciona tu país…</option>
              {countries.map((c) => (
                <option key={c.country} value={c.country}>
                  {c.country}
                </option>
              ))}
            </select>
          </Field>
          <Field icon={School} label="Universidad">
            <select
              data-testid="onboarding-university"
              value={university}
              onChange={(e) => {
                const opt = universities.find((u) => u.university_name === e.target.value);
                setUniversity(e.target.value, opt?.catalog_id || null);
              }}
              disabled={!country}
              className="w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 disabled:opacity-50"
            >
              <option value="">
                {country ? "Selecciona tu universidad…" : "Primero elige un país"}
              </option>
              {universities.map((u) => (
                <option key={u.catalog_id} value={u.university_name}>
                  {u.university_name}
                </option>
              ))}
            </select>
            {country && universities.length === 0 && (
              <p className="mt-1 text-[11.5px] text-ink-soft">
                Aún no tenemos plantillas para este país. Puedes seguir y cargar el formato manualmente más adelante.
              </p>
            )}
          </Field>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 2 ---------- */
function Step2({ career, setCareer, title, setTitle }) {
  return (
    <div data-testid="onboarding-step-2">
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Paso 2 — Tu tesis
      </p>
      <h3 className="mt-2 font-serif text-2xl text-ink leading-tight">
        Cuéntanos sobre tu proyecto
      </h3>
      <p className="mt-2 text-[13px] text-ink-soft">
        Estos datos personalizan al copiloto y al simulador de jurado.
      </p>

      <div className="mt-6 flex flex-col gap-4">
        <Field icon={BookOpen} label="Carrera o Facultad">
          <input
            data-testid="onboarding-career"
            value={career}
            onChange={(e) => setCareer(e.target.value)}
            placeholder="Ej. Licenciatura en Psicología"
            className="w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
          />
        </Field>
        <Field icon={Sparkles} label="Título tentativo de la tesis">
          <textarea
            data-testid="onboarding-thesis-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej. Impacto del aprendizaje invertido en estudiantes de licenciatura"
            rows={2}
            maxLength={200}
            className="w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 resize-none"
          />
          <p className="mt-1 text-[11px] text-ink-soft text-right">{title.length}/200</p>
        </Field>
      </div>
    </div>
  );
}

/* ---------- Step 3 ---------- */
function Step3({ stages, value, setValue }) {
  return (
    <div data-testid="onboarding-step-3">
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Paso 3 — Tu punto de partida
      </p>
      <h3 className="mt-2 font-serif text-2xl text-ink leading-tight">
        ¿En qué etapa vas?
      </h3>
      <p className="mt-2 text-[13px] text-ink-soft">
        Adaptamos el editor y las sugerencias del copiloto a tu momento.
      </p>

      <div className="mt-6 flex flex-col gap-3" data-testid="onboarding-stages">
        {(stages || []).map((s) => {
          const Icon = STAGE_ICONS[s.id] || Rocket;
          const active = value === s.id;
          return (
            <button
              key={s.id}
              data-testid={`onboarding-stage-${s.id}`}
              onClick={() => setValue(s.id)}
              className={`group flex items-start gap-4 text-left rounded-2xl border p-4 transition-all ${
                active
                  ? "border-forest bg-white ring-2 ring-forest/15"
                  : "border-rule bg-white/60 hover:bg-white"
              }`}
            >
              <span
                className={`grid place-items-center h-10 w-10 rounded-xl ${
                  active ? "bg-forest text-paper" : "bg-paper-2 text-forest"
                }`}
              >
                <Icon size={16} />
              </span>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-serif text-lg text-ink leading-tight">{s.label}</p>
                  {active && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-forest/10 text-forest text-[11px] font-medium px-2 py-0.5">
                      <Check size={11} /> Elegido
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[12.5px] text-ink-soft leading-relaxed">
                  {STAGE_DESCRIPTIONS[s.id] || ""}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Field wrapper ---------- */
function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium mb-1.5">
        <Icon size={11} /> {label}
      </span>
      {children}
    </label>
  );
}
